import { Phone, MessageSquareText, Mail, Download, Share2, Globe, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { PHONE, PHONE_RAW, EMAIL } from "./shared";

export const businessCardUrl = () => `${window.location.origin}/card`;

const VCARD = [
  "BEGIN:VCARD",
  "VERSION:3.0",
  "N:Fisher;Eric;;;",
  "FN:Eric Fisher — Friday Stumps",
  "ORG:Friday Stumps",
  "TITLE:Owner",
  `TEL;TYPE=CELL:+1${PHONE_RAW}`,
  `EMAIL;TYPE=INTERNET:${EMAIL}`,
  "URL:https://fridaystumps.com",
  "ADR;TYPE=WORK:;;Columbus;OH;;;",
  "NOTE:Stump grinding & forestry mulching. Fridays only. Columbus OH.",
  "END:VCARD",
].join("\r\n");

export function downloadVCard() {
  const blob = new Blob([VCARD], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "eric-fisher-fridaystumps.vcf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ContactRow({ icon: Icon, label, value, href, testId }) {
  return (
    <a
      href={href}
      data-testid={testId}
      className="flex min-h-[48px] items-center gap-3.5 border border-stone-700/80 bg-black/20 px-4 py-3 transition-colors duration-150 hover:border-amber-500 active:border-amber-500"
    >
      <Icon className="h-4 w-4 shrink-0 text-amber-500" />
      <span className="min-w-0">
        <span className="block font-mono text-[9px] uppercase tracking-[0.22em] text-stone-500">{label}</span>
        <span className="block truncate text-sm font-semibold text-bone">{value}</span>
      </span>
    </a>
  );
}

export default function BusinessCard() {
  const cardUrl = businessCardUrl();

  const shareCard = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Eric Fisher — Friday Stumps",
          text: "Stump grinding & forestry mulching, Columbus OH. Fridays only.",
          url: cardUrl,
        });
      } catch {
        /* user dismissed the share sheet */
      }
    } else {
      try {
        await navigator.clipboard.writeText(cardUrl);
        toast.success("Card link copied.");
      } catch {
        toast.error("Couldn't copy — long-press the link instead.");
      }
    }
  };

  return (
    <div data-testid="card-page" className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="overflow-hidden border border-amber-600/70 bg-charcoal shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
          <div className="h-1.5 bg-amber-500" />
          <div className="p-7 sm:p-9">
            <div className="flex items-start justify-between gap-4">
              <p className="font-display text-4xl font-black uppercase leading-[0.9] tracking-tight text-bone sm:text-5xl">
                Friday
                <br />
                <span className="text-amber-500">Stumps</span>
                <span className="text-lg text-stone-500">.com</span>
              </p>
              <img
                src="/brand/fridaystumps-logo-transparent.png"
                alt="Friday Stumps logo"
                data-testid="card-logo"
                className="h-16 w-16 shrink-0 object-contain sm:h-20 sm:w-20"
              />
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
              Stump grinding · Forestry mulching · Fridays only · Columbus OH
            </p>
            <div className="my-6 h-px bg-amber-500/40" />
            <p data-testid="card-name" className="font-display text-2xl font-black uppercase tracking-tight text-bone">
              Eric Fisher
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
              Owner — the guy who shows up
            </p>

            <div className="mt-7 space-y-2.5">
              <ContactRow icon={Phone} label="Call me" value={PHONE} href={`tel:+1${PHONE_RAW}`} testId="card-call-btn" />
              <ContactRow icon={MessageSquareText} label="Text me — fastest" value={PHONE} href={`sms:+1${PHONE_RAW}`} testId="card-text-btn" />
              <ContactRow icon={Mail} label="Email" value={EMAIL} href={`mailto:${EMAIL}`} testId="card-email-btn" />
              <ContactRow icon={Globe} label="Get a quote online" value="fridaystumps.com" href="/" testId="card-site-link" />
            </div>

            <div className="mt-7 flex flex-col gap-2.5 sm:flex-row">
              <button
                data-testid="card-save-contact-btn"
                onClick={downloadVCard}
                className="flex min-h-[48px] flex-1 items-center justify-center gap-2 bg-amber-500 px-4 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600"
              >
                <Download className="h-3.5 w-3.5" /> Save to contacts
              </button>
              <button
                data-testid="card-share-btn"
                onClick={shareCard}
                className="flex min-h-[48px] flex-1 items-center justify-center gap-2 border border-stone-600 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500"
              >
                <Share2 className="h-3.5 w-3.5" /> Share this card
              </button>
            </div>
          </div>
          <div className="h-1.5 bg-amber-500" />
        </div>

        <a
          href="/"
          data-testid="card-back-link"
          className="mt-6 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500 transition-colors duration-150 hover:text-amber-500"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> fridaystumps.com
        </a>
      </div>
    </div>
  );
}
